/**
 * Hook de Observabilidade por Tenant
 * 
 * Fornece dados de observabilidade filtrados por tenant específico:
 * - Estatísticas gerais (taxa de sucesso, latência média)
 * - Logs de requisições com filtros
 * - Métricas por provider
 * - Volume temporal de requisições
 * 
 * IMPORTANTE: Usa RPCs específicas por tenant para agregação no banco,
 * garantindo isolamento de dados multi-tenant.
 * 
 * @version 1.0.0
 * @release 2026-01-19
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import type { 
  ObservabilityStats, 
  ApiRequestLog, 
  VolumeDataPoint, 
  LatencyDataPoint,
  ObservabilityFilters 
} from './useObservability';

// ============= Hooks Específicos por Tenant =============

/**
 * Hook para buscar estatísticas de observabilidade de um tenant específico
 * Usa RPCs filtradas por tenant para agregação no banco
 * 
 * @param tenantId - ID do tenant para filtrar
 * @param hours - Período em horas (padrão: 24h)
 */
export function useTenantObservabilityStats(tenantId: string | null, hours: number = 24) {
  return useQuery({
    queryKey: ['tenant-observability-stats', tenantId, hours],
    queryFn: async () => {
      if (!tenantId) {
        logger.warn('useTenantObservabilityStats', 'tenantId é obrigatório');
        return null;
      }

      logger.debug('useTenantObservabilityStats', 'Buscando estatísticas', { tenantId, hours });

      // Chamar as 3 RPCs específicas por tenant em paralelo
      const [statsResult, providerResult, sourceResult] = await Promise.all([
        supabase.rpc('get_observability_stats_by_tenant', { 
          p_tenant_id: tenantId, 
          p_hours_ago: hours 
        }),
        supabase.rpc('get_observability_by_provider_by_tenant', { 
          p_tenant_id: tenantId, 
          p_hours_ago: hours 
        }),
        supabase.rpc('get_observability_by_source_by_tenant', { 
          p_tenant_id: tenantId, 
          p_hours_ago: hours 
        }),
      ]);

      if (statsResult.error) {
        logger.error('useTenantObservabilityStats', 'Erro ao buscar stats', { error: statsResult.error });
        throw statsResult.error;
      }
      if (providerResult.error) {
        logger.error('useTenantObservabilityStats', 'Erro ao buscar providers', { error: providerResult.error });
        throw providerResult.error;
      }
      if (sourceResult.error) {
        logger.error('useTenantObservabilityStats', 'Erro ao buscar sources', { error: sourceResult.error });
        throw sourceResult.error;
      }

      // Processar resultado das estatísticas gerais
      const statsData = statsResult.data?.[0] || {
        total_requests: 0,
        success_count: 0,
        error_count: 0,
        inbound_count: 0,
        outbound_count: 0,
        avg_latency_ms: 0,
      };

      const total = Number(statsData.total_requests) || 0;
      const success = Number(statsData.success_count) || 0;
      const errors = Number(statsData.error_count) || 0;

      // Processar dados por provider
      const byProvider = (providerResult.data || []).map((p: {
        provider: string;
        total_count: number;
        success_count: number;
        avg_latency_ms: number;
      }) => ({
        provider: p.provider,
        count: Number(p.total_count) || 0,
        successRate: p.total_count > 0 ? (Number(p.success_count) / Number(p.total_count)) * 100 : 0,
        avgLatencyMs: Number(p.avg_latency_ms) || 0,
      }));

      // Processar dados por source
      const bySource = (sourceResult.data || []).map((s: { source: string; count: number }) => ({
        source: s.source,
        count: Number(s.count) || 0,
      }));

      const stats: ObservabilityStats = {
        totalRequests24h: total,
        successRate: total > 0 ? (success / total) * 100 : 100,
        errorRate: total > 0 ? (errors / total) * 100 : 0,
        avgLatencyMs: Number(statsData.avg_latency_ms) || 0,
        inboundCount: Number(statsData.inbound_count) || 0,
        outboundCount: Number(statsData.outbound_count) || 0,
        byProvider,
        bySource,
      };

      logger.debug('useTenantObservabilityStats', 'Estatísticas processadas', { 
        total, success, errors, providers: byProvider.length 
      });

      return stats;
    },
    enabled: !!tenantId,
    refetchInterval: 30000, // Atualizar a cada 30 segundos
  });
}

/**
 * Hook para buscar logs de requisições de um tenant específico
 * Reutiliza a lógica de useApiRequestLogs mas força o filtro de tenant
 * 
 * @param tenantId - ID do tenant para filtrar
 * @param filters - Filtros adicionais
 * @param page - Página atual
 * @param pageSize - Itens por página
 */
export function useTenantApiRequestLogs(
  tenantId: string | null,
  filters: Omit<ObservabilityFilters, 'tenantId'> = {},
  page: number = 1,
  pageSize: number = 25
) {
  return useQuery({
    queryKey: ['tenant-api-request-logs', tenantId, filters, page, pageSize],
    queryFn: async () => {
      if (!tenantId) {
        logger.warn('useTenantApiRequestLogs', 'tenantId é obrigatório');
        return { logs: [], total: 0, page: 1, pageSize, totalPages: 0 };
      }

      logger.debug('useTenantApiRequestLogs', 'Buscando logs', { tenantId, filters, page, pageSize });

      // Período padrão: últimas 24h
      const startDate = filters.startDate || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const endDate = filters.endDate || new Date().toISOString();

      let query = supabase
        .from('api_request_logs')
        .select('*', { count: 'exact' })
        .eq('tenant_id', tenantId) // Filtro fixo por tenant
        .gte('created_at', startDate)
        .lte('created_at', endDate)
        .order('created_at', { ascending: false })
        .range((page - 1) * pageSize, page * pageSize - 1);

      // Aplicar filtros adicionais
      if (filters.provider && filters.provider !== 'all') {
        query = query.eq('provider_slug', filters.provider);
      }
      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters.source && filters.source !== 'all') {
        query = query.eq('source_type', filters.source);
      }
      if (filters.direction && filters.direction !== 'all') {
        query = query.eq('direction', filters.direction);
      }
      if (filters.search) {
        query = query.ilike('error_message', `%${filters.search}%`);
      }
      if (filters.phone) {
        const phoneDigits = filters.phone.replace(/\D/g, '');
        if (phoneDigits) {
          query = query.ilike('request_payload->>phone', `%${phoneDigits}%`);
        }
      }

      const { data, error, count } = await query;

      if (error) {
        logger.error('useTenantApiRequestLogs', 'Erro ao buscar logs', { error });
        throw error;
      }

      // Converter para tipo ApiRequestLog
      const logs: ApiRequestLog[] = (data || []).map(log => ({
        ...log,
        request_payload: log.request_payload as Record<string, unknown> | null,
        request_headers: log.request_headers as Record<string, unknown> | null,
        response_payload: log.response_payload as Record<string, unknown> | null,
      }));

      logger.debug('useTenantApiRequestLogs', 'Logs carregados', { count: logs.length, total: count });

      return {
        logs,
        total: count || 0,
        page,
        pageSize,
        totalPages: Math.ceil((count || 0) / pageSize),
      };
    },
    enabled: !!tenantId,
    refetchInterval: 30000,
  });
}

/**
 * Hook para buscar volume de requisições por hora de um tenant específico
 * Usa RPC específica por tenant para agregação no banco
 * 
 * @param tenantId - ID do tenant para filtrar
 * @param hours - Período em horas (padrão: 24h)
 */
export function useTenantRequestVolume(tenantId: string | null, hours: number = 24) {
  return useQuery({
    queryKey: ['tenant-request-volume', tenantId, hours],
    queryFn: async () => {
      if (!tenantId) {
        logger.warn('useTenantRequestVolume', 'tenantId é obrigatório');
        return [];
      }

      logger.debug('useTenantRequestVolume', 'Buscando volume', { tenantId, hours });

      const { data, error } = await supabase.rpc('get_observability_volume_hourly_by_tenant', { 
        p_tenant_id: tenantId, 
        p_hours_ago: hours 
      });

      if (error) {
        logger.error('useTenantRequestVolume', 'Erro ao buscar volume', { error });
        throw error;
      }

      // Converter para formato esperado pelo componente
      const volumeData: VolumeDataPoint[] = (data || []).map((item: {
        hour_label: string;
        success_count: number;
        error_count: number;
      }) => ({
        hour: item.hour_label,
        success: Number(item.success_count) || 0,
        error: Number(item.error_count) || 0,
        total: (Number(item.success_count) || 0) + (Number(item.error_count) || 0),
      }));

      logger.debug('useTenantRequestVolume', 'Volume processado', { points: volumeData.length });

      return volumeData;
    },
    enabled: !!tenantId,
    refetchInterval: 60000,
  });
}

/**
 * Hook para buscar latência por provider de um tenant específico
 * Usa RPC específica por tenant (inclui P95 real calculado pelo PostgreSQL)
 * 
 * @param tenantId - ID do tenant para filtrar
 * @param hours - Período em horas (padrão: 24h)
 */
export function useTenantLatencyByProvider(tenantId: string | null, hours: number = 24) {
  return useQuery({
    queryKey: ['tenant-latency-by-provider', tenantId, hours],
    queryFn: async () => {
      if (!tenantId) {
        logger.warn('useTenantLatencyByProvider', 'tenantId é obrigatório');
        return [];
      }

      logger.debug('useTenantLatencyByProvider', 'Buscando latência', { tenantId, hours });

      const { data, error } = await supabase.rpc('get_observability_by_provider_by_tenant', { 
        p_tenant_id: tenantId, 
        p_hours_ago: hours 
      });

      if (error) {
        logger.error('useTenantLatencyByProvider', 'Erro ao buscar latência', { error });
        throw error;
      }

      // Converter para formato esperado pelo componente
      const latencyData: LatencyDataPoint[] = (data || []).map((item: {
        provider: string;
        avg_latency_ms: number;
        p95_latency_ms: number;
      }) => ({
        provider: item.provider,
        avgLatency: Math.round(Number(item.avg_latency_ms) || 0),
        p95Latency: Math.round(Number(item.p95_latency_ms) || 0),
        maxLatency: 0, // Não usado, P95 é mais relevante
      }));

      logger.debug('useTenantLatencyByProvider', 'Latência processada', { providers: latencyData.length });

      return latencyData;
    },
    enabled: !!tenantId,
    refetchInterval: 60000,
  });
}
